// Eszköz-IGÉNY olvasó-nézete — az igény-sáv (`asset-request-sheet`) NÉZET módjának törzse. A sorrend és a megjelenítés
// a lista-oldalt követi (egy forrás: `asset-cells.tsx`): eszköz (alatta a termék-link) · típus a kategória-jelvénnyel ·
// ár nettó / áfa / bruttó bontásban · kinek (alatta a csapata) · felhasználás helye · igénylő · státusz · megjegyzés.
// A megjelenítés tiszta (nincs lekérés, nincs mutáció).
import { type AssetRowSchema } from '@atlas/contracts';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { AssetPriceBreakdown, AssetTypeWithCategory } from '@/components/assets/asset-cells';
import { AssetStatusBadge } from '@/components/assets/asset-status-badge';
import { PersonChip } from '@/components/shared/person-chip';
import { TeamBadge } from '@/components/shared/team-badge';
import { type AssetCatalogLookup } from '@/features/assets/catalog';
import { priceParts } from '@/features/assets/price';
import { isExternalUrl, toHref } from '@/lib/external-url';

type AssetRow = z.infer<typeof AssetRowSchema>;

export interface AssetRequestDetailsProps {
  request: AssetRow;
  lookup: AssetCatalogLookup;
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-3 sm:gap-3">
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="min-w-0 break-words text-sm sm:col-span-2">{children}</dd>
    </div>
  );
}

export function AssetRequestDetails({ request, lookup }: AssetRequestDetailsProps): JSX.Element {
  const { t } = useTranslation();
  const dash = <span className="text-muted-foreground">—</span>;

  // KINEK: iroda → fix felirat; különben a kedvezményezett, önmagának igénylésnél az igénylő (az ő avatarjával)
  const forOther = request.requestedForName !== null;
  const recipientName = forOther ? request.requestedForName : request.requesterName;

  return (
    <dl className="space-y-4">
      <Row label={t('assets.name')}>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="font-medium">{request.name}</span>
          {request.website &&
            (isExternalUrl(request.website) ? (
              <a
                href={toHref(request.website)}
                target="_blank"
                rel="noreferrer"
                className="text-brand-hover truncate text-xs hover:underline"
              >
                {request.website}
              </a>
            ) : (
              <span className="text-muted-foreground truncate text-xs">{request.website}</span>
            ))}
        </div>
      </Row>
      <Row label={t('assets.type')}>
        <AssetTypeWithCategory type={request.type} category={request.category} lookup={lookup} />
      </Row>
      <Row label={t('assets.price')}>
        {priceParts(request.netPrice, request.vatRate) ? (
          <AssetPriceBreakdown netPrice={request.netPrice} vatRate={request.vatRate} />
        ) : (
          dash
        )}
      </Row>
      <Row label={t('assets.request.kinekCol')}>
        <div className="flex min-w-0 flex-col items-start gap-1">
          {request.officeUse ? (
            <span>{t('assets.request.officeRecipient')}</span>
          ) : recipientName ? (
            <PersonChip
              givenName={forOther ? request.requestedForGivenName : request.requesterGivenName}
              familyName={forOther ? request.requestedForFamilyName : request.requesterFamilyName}
              name={recipientName}
              avatarUrl={forOther ? null : request.requesterAvatarUrl}
              accentColor={forOther ? null : request.requesterTeamColor}
              size="sm"
              className="gap-1.5"
            />
          ) : (
            dash
          )}
          {request.teamName && (
            <TeamBadge name={request.teamName} color={request.teamColor} className="max-w-full" />
          )}
        </div>
      </Row>
      <Row label={t('assets.usageLocationCol')}>
        {request.usageLocation
          ? t(`assets.usageLabel.${request.usageLocation}`, { defaultValue: request.usageLocation })
          : dash}
      </Row>
      <Row label={t('assets.requesterCol')}>
        {request.requesterName ? (
          <PersonChip
            givenName={request.requesterGivenName}
            familyName={request.requesterFamilyName}
            name={request.requesterName}
            avatarUrl={request.requesterAvatarUrl}
            accentColor={request.requesterTeamColor}
            size="sm"
            className="gap-1.5"
          />
        ) : (
          dash
        )}
      </Row>
      <Row label={t('assets.status')}>
        <AssetStatusBadge
          status={request.status}
          label={t(`assets.statusLabel.${request.status}`, { defaultValue: request.status })}
        />
      </Row>
      <Row label={t('assets.request.fields.note')}>
        {request.requestNote ? (
          <span className="whitespace-pre-line">{request.requestNote}</span>
        ) : (
          dash
        )}
      </Row>
    </dl>
  );
}
